import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";
import { Spinner } from "@/components/ui/spinner";

const buttonVariants = cva(
  // whitespace-nowrap: the heights below are fixed, so a label that wraps
  // spills out of the button. The row around it should wrap instead.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium " +
    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent " +
    "disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-accent text-fg-on-accent hover:bg-accent-hover",
        secondary: "bg-bg-elevated text-fg-primary hover:bg-bg-elevated/80",
        ghost: "hover:bg-bg-elevated/50 text-fg-primary",
        danger: "bg-danger text-fg-on-danger hover:bg-danger/90",
        outline:
          "border border-border bg-transparent text-fg-primary hover:bg-bg-elevated/50",
        link: "text-accent underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 px-3",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Show a spinner and disable the button while busy. */
  loading?: boolean;
  /** Optional leading icon (hidden while loading — the spinner takes its slot). */
  iconLeft?: ReactNode;
  /** Optional trailing icon. */
  iconRight?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading = false,
      iconLeft,
      iconRight,
      disabled,
      children,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner size="sm" /> : iconLeft}
      {children}
      {iconRight}
    </button>
  ),
);
Button.displayName = "Button";
